import { clsx } from 'clsx';
import { Link, navigate } from 'gatsby';
import PreorderNotice from '@/components/PreorderNotice/PreorderNotice';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import {
  closeSheet,
  removeItem,
  updateQuantity,
  selectCartItems,
  selectIsSheetOpen,
} from '@/features/cart/cart-slice';
import {
  GST_RATE,
  parsePrice,
  formatCurrency,
  getCartItemKey,
} from '@/utils/cart-math';

const CartSheet = () => {
  const dispatch = useAppDispatch();
  const items = useAppSelector(selectCartItems);
  const isOpen = useAppSelector(selectIsSheetOpen);

  const subtotal = items.reduce(
    (sum, item) => sum + parsePrice(item.price) * item.quantity,
    0,
  );
  const gst = Math.round(subtotal * GST_RATE * 100) / 100;
  const total = subtotal + gst;

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={() => dispatch(closeSheet())}
        aria-hidden="true"
      />

      <div
        className={clsx(
          `fixed right-0 top-0 bottom-0 z-50`,
          `w-full sm:max-w-[480px]`,
          `bg-primary-400 flex flex-col`,
          `shadow-2xl`,
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Cart"
      >
        <div className="flex items-center justify-between px-5 py-4 bg-primary-600 flex-shrink-0">
          <h2 className="text-xl font-bold mb-0 text-white">Your Cart</h2>
          <button
            type="button"
            onClick={() => dispatch(closeSheet())}
            aria-label="Close cart"
            className="text-3xl leading-none text-white/60 hover:text-white transition-colors"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-5">
              <p className="text-sm text-primary-300 mb-4">
                Your cart is empty.
              </p>
              <Link
                to="/#shop"
                className="text-sm text-accent-400 hover:text-accent-300 underline"
                onClick={() => dispatch(closeSheet())}
              >
                Browse our shop →
              </Link>
            </div>
          ) : (
            <>
              <div className="px-5 pt-5">
                <p className="text-xs font-bold uppercase tracking-wider text-primary-300 mb-3">
                  Order Summary
                </p>
                <ul className="space-y-4">
                  {items.map((item) => {
                    const key = getCartItemKey(item);
                    return (
                      <li
                        key={key}
                        className="flex gap-3 pb-4 border-b border-primary-500"
                      >
                        {item.coverImageSrc ? (
                          <img
                            src={item.coverImageSrc}
                            alt={item.title}
                            className="w-14 h-20 object-cover flex-shrink-0 rounded shadow-md"
                          />
                        ) : (
                          <div className="w-14 h-20 flex-shrink-0 rounded bg-primary-500 flex items-center justify-center">
                            <span className="text-primary-300 text-xs text-center leading-tight px-1">
                              {item.title.slice(0, 2)}
                            </span>
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white leading-tight mb-1">
                            {item.title}
                          </p>
                          {item.bundleSelections ? (
                            <p className="text-xs text-primary-300 mb-1">
                              {item.bundleSelections.firstBook} +{` `}
                              {item.bundleSelections.secondBook}
                            </p>
                          ) : null}
                          {item.isPreorder ? (
                            <PreorderNotice
                              variant="tag"
                              message={item.preOrderMessage}
                              className="mb-1"
                            />
                          ) : null}
                          <div className="flex items-center gap-2 mt-2">
                            <label
                              htmlFor={`cart-qty-${key}`}
                              className="text-xs text-primary-300 mb-0"
                            >
                              Qty
                            </label>
                            <input
                              id={`cart-qty-${key}`}
                              type="number"
                              min={1}
                              max={99}
                              value={item.quantity}
                              className={clsx(
                                `w-14 px-2 py-1 text-sm text-right`,
                                `bg-primary-500 border border-primary-400 rounded`,
                                `text-white`,
                              )}
                              onChange={(e) => {
                                const n = Number.parseInt(e.target.value, 10);
                                dispatch(
                                  updateQuantity({
                                    itemKey: key,
                                    quantity: Number.isNaN(n) ? 1 : n,
                                  }),
                                );
                              }}
                            />
                            <span className="ml-auto text-sm font-semibold text-white">
                              {formatCurrency(
                                parsePrice(item.price) * item.quantity,
                              )}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="mt-2 text-xs text-primary-300 underline hover:text-white transition-colors"
                            onClick={() => dispatch(removeItem(key))}
                          >
                            Remove
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="px-5 py-4 bg-primary-600 mt-3">
                <div className="flex justify-between text-sm text-primary-300 mb-1">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-primary-300 mb-1">
                  <span>GST (5%)</span>
                  <span>{formatCurrency(gst)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-white pt-2 border-t border-primary-500">
                  <span>Total CAD</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>

              <div className="px-5 py-4">
                <button
                  type="button"
                  className={clsx(
                    `w-full px-6 py-3 rounded-full text-center font-bold transition-all`,
                    `bg-accent-500 text-black hover:bg-accent-400`,
                  )}
                  onClick={() => {
                    dispatch(closeSheet());
                    navigate(`/checkout`);
                  }}
                >
                  Checkout
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSheet;
